import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { createNotification } from '@/lib/notifications';
import { useAuth } from '@/hooks/useAuth';
import { formatDate } from '@/lib/dateUtils';

export type TaskType = 'general' | 'decision' | 'selection' | 'payment' | 'approval' | 'information';
export type TaskStatus = 'pending' | 'awaiting_response' | 'completed' | 'overdue' | 'cancelled';
export type AssigneeRole = 'management' | 'subcontractor' | 'client' | 'all';
export type PaymentStatus = 'not_required' | 'unpaid' | 'partial' | 'paid' | 'overdue';

export interface TaskOption {
  id: string;
  task_id: string;
  label: string;
  description: string | null;
  image_url: string | null;
  price_note: string | null;
  sort_order: number;
}

export interface ProjectTask {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  task_type: TaskType;
  assignee_role: AssigneeRole;
  status: TaskStatus;
  due_date: string | null;
  invoice_number: string | null;
  invoice_amount: number | null;
  payment_due_date: string | null;
  payment_status: PaymentStatus | null;
  payment_reference: string | null;
  payment_note: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  options?: TaskOption[];
  myResponse?: TaskResponse | null;
}

export interface TaskResponse {
  id: string;
  task_id: string;
  responded_by: string;
  selected_option_id: string | null;
  response_text: string | null;
  created_at: string;
}

type FilterTab = 'All' | 'Open' | 'Decision' | 'Payment' | 'Overdue' | 'Completed';

interface TasksTabProps {
  projectId: string;
  canCreate?: boolean;
  viewerRole?: 'management' | 'subcontractor' | 'client';
}

export default function TasksTab({ projectId, canCreate = true, viewerRole = 'management' }: TasksTabProps) {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // ── Translated configs (reactive to language) ──
  const taskTypeConfig: Record<TaskType, { label: string; icon: string; color: string; bg: string }> = {
    general:     { label: t('tasks.typeGeneral'),     icon: 'ri-task-line',            color: '#374151', bg: '#F9FAFB' },
    decision:    { label: t('tasks.typeDecision'),    icon: 'ri-git-branch-line',      color: '#7C3AED', bg: '#F5F3FF' },
    selection:   { label: t('tasks.typeSelection'),   icon: 'ri-palette-line',         color: '#E8621A', bg: '#FEF0E7' },
    payment:     { label: t('tasks.typePayment'),     icon: 'ri-bank-card-line',       color: '#059669', bg: '#ECFDF5' },
    approval:    { label: t('tasks.typeApproval'),    icon: 'ri-checkbox-circle-line', color: '#0891B2', bg: '#F0F9FF' },
    information: { label: t('tasks.typeInformation'), icon: 'ri-information-line',     color: '#9CA3AF', bg: '#F9FAFB' },
  };

  const taskStatusConfig: Record<TaskStatus, { label: string; bg: string; text: string }> = {
    pending:           { label: t('tasks.statusPending'),          bg: '#F3F4F6', text: '#6B7280' },
    awaiting_response: { label: t('tasks.statusAwaitingResponse'), bg: '#FEF0E7', text: '#E8621A' },
    completed:         { label: t('tasks.statusCompleted'),        bg: '#F0FDF4', text: '#059669' },
    overdue:           { label: t('tasks.statusOverdue'),          bg: '#FEF2F2', text: '#EF4444' },
    cancelled:         { label: t('tasks.statusCancelled'),        bg: '#F9FAFB', text: '#9CA3AF' },
  };

  const paymentStatusConfig: Record<PaymentStatus, { label: string; bg: string; text: string }> = {
    not_required: { label: t('tasks.payStatusNA'),      bg: '#F9FAFB', text: '#9CA3AF' },
    unpaid:       { label: t('tasks.payStatusUnpaid'),  bg: '#FEF2F2', text: '#EF4444' },
    partial:      { label: t('tasks.payStatusPartial'), bg: '#FFFBEB', text: '#F59E0B' },
    paid:         { label: t('tasks.payStatusPaid'),    bg: '#F0FDF4', text: '#059669' },
    overdue:      { label: t('tasks.payStatusOverdue'), bg: '#FEF2F2', text: '#EF4444' },
  };

  const assigneeConfig: Record<AssigneeRole, { label: string; color: string; icon: string }> = {
    management:    { label: t('tasks.roleManagement'),    color: '#E8621A', icon: 'ri-building-2-line' },
    subcontractor: { label: t('tasks.roleSubcontractor'), color: '#059669', icon: 'ri-tools-line' },
    client:        { label: t('tasks.roleClient'),        color: '#374151', icon: 'ri-user-line' },
    all:           { label: t('tasks.roleAllParties'),    color: '#9CA3AF', icon: 'ri-group-line' },
  };

  const filterTabs: { key: FilterTab; label: string }[] = [
    { key: 'All',       label: t('tasks.filterAll') },
    { key: 'Open',      label: t('tasks.filterOpen') },
    { key: 'Decision',  label: t('tasks.filterDecision') },
    { key: 'Payment',   label: t('tasks.filterPayment') },
    { key: 'Overdue',   label: t('tasks.filterOverdue') },
    { key: 'Completed', label: t('tasks.filterCompleted') },
  ];

  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterTab>('All');
  const [selectedTask, setSelectedTask] = useState<ProjectTask | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formTitle, setFormTitle] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formType, setFormType] = useState<TaskType>('general');
  const [formRole, setFormRole] = useState<AssigneeRole>('client');
  const [formDue, setFormDue] = useState('');
  const [formOptions, setFormOptions] = useState<{ label: string; description: string; price_note: string }[]>([]);
  const [newOptLabel, setNewOptLabel] = useState('');
  const [newOptDesc, setNewOptDesc] = useState('');
  const [newOptPrice, setNewOptPrice] = useState('');
  const [formInvNum, setFormInvNum] = useState('');
  const [formInvAmt, setFormInvAmt] = useState('');
  const [formPayDue, setFormPayDue] = useState('');
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    if (projectId) loadTasks();
  }, [projectId]);

  async function loadTasks() {
    setLoading(true);

    const { data: taskData } = await supabase
      .from('jphph_tasks')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    const { data: connectTasks } = await supabase
      .from('jphc_tasks')
      .select('id, title, description, due_date, status, priority, assigned_to, created_by, created_at, updated_at, portal_project_id')
      .eq('portal_project_id', projectId)
      .order('created_at', { ascending: false });

    const mappedConnectTasks: ProjectTask[] = (connectTasks || []).map((ct) => ({
      id: `jphc-${ct.id}`,
      project_id: projectId,
      title: `[Connect] ${ct.title}`,
      description: ct.description || null,
      task_type: 'general' as TaskType,
      assignee_role: 'management' as AssigneeRole,
      status: ct.status === 'Completed' ? 'completed' : ct.status === 'In Progress' ? 'awaiting_response' : 'pending' as TaskStatus,
      due_date: ct.due_date || null,
      invoice_number: null,
      invoice_amount: null,
      payment_due_date: null,
      payment_status: null,
      payment_reference: null,
      payment_note: null,
      created_by: ct.created_by || null,
      created_at: ct.created_at,
      updated_at: ct.updated_at,
      options: [],
      myResponse: null,
    }));

    if (!taskData && mappedConnectTasks.length === 0) { setLoading(false); return; }

    const taskIds = (taskData || []).map((t) => t.id);

    const [optionsRes, responsesRes] = await Promise.all([
      taskIds.length > 0
        ? supabase.from('jphph_task_options').select('*').in('task_id', taskIds).order('sort_order', { ascending: true })
        : Promise.resolve({ data: [] }),
      user && taskIds.length > 0
        ? supabase.from('jphph_task_responses').select('*').in('task_id', taskIds).eq('responded_by', user.id)
        : Promise.resolve({ data: [] }),
    ]);

    const optionsByTask: Record<string, TaskOption[]> = {};
    (optionsRes.data || []).forEach((o) => {
      if (!optionsByTask[o.task_id]) optionsByTask[o.task_id] = [];
      optionsByTask[o.task_id].push(o as TaskOption);
    });

    const responsesByTask: Record<string, TaskResponse> = {};
    (responsesRes.data || []).forEach((r) => {
      responsesByTask[r.task_id] = r as TaskResponse;
    });

    const enriched: ProjectTask[] = (taskData || []).map((t) => ({
      ...t,
      options: optionsByTask[t.id] || [],
      myResponse: responsesByTask[t.id] || null,
    }));

    setTasks([...enriched as ProjectTask[], ...mappedConnectTasks]);
    setLoading(false);
  }

  async function handleCreate() {
    if (!formTitle.trim() || !user) return;
    setSaving(true);
    setCreateError('');
    try {
      const { data: newTask, error } = await supabase.from('jphph_tasks').insert({
        project_id: projectId,
        title: formTitle.trim(),
        description: formDesc || null,
        task_type: formType,
        assignee_role: formRole,
        status: 'pending',
        due_date: formDue || null,
        invoice_number: formInvNum || null,
        invoice_amount: formInvAmt ? parseFloat(formInvAmt) : null,
        payment_due_date: formPayDue || null,
        payment_status: formType === 'payment' ? 'unpaid' : null,
        created_by: user.id,
      }).select().maybeSingle();
      if (error) throw error;
      if (newTask && formOptions.length > 0) {
        await supabase.from('jphph_task_options').insert(
          formOptions.map((o, i) => ({
            task_id: newTask.id,
            label: o.label,
            description: o.description || null,
            price_note: o.price_note || null,
            sort_order: i,
          }))
        );
      }
      await supabase.from('jphc_tasks').insert({
        title: formTitle.trim(),
        description: formDesc || null,
        due_date: formDue || null,
        status: 'Pending',
        priority: 'Medium',
        created_by: user.id,
        portal_project_id: projectId,
      });

      await createNotification({
        project_id: projectId,
        notification_type: 'task',
        title: `New task: ${formTitle.trim()}`,
        body: formDesc || `A new ${formType} task has been assigned to you.`,
        target_role: formRole === 'all' ? 'subcontractor' : formRole,
        link_path: `/management/projects/${projectId}`,
        target_type: 'task',
        target_id: newTask.id,
        created_by: user.id,
      });

      resetCreateForm();
      setShowCreateModal(false);
      await loadTasks();
    } catch (err: unknown) {
      setCreateError(err instanceof Error ? err.message : t('error.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  function resetCreateForm() {
    setFormTitle(''); setFormDesc(''); setFormType('general'); setFormRole('client');
    setFormDue(''); setFormOptions([]); setFormInvNum(''); setFormInvAmt(''); setFormPayDue('');
    setCreateError('');
  }

  async function handleSelectOption(taskId: string, optionId: string) {
    if (!user) return;
    await supabase.from('jphph_task_responses').upsert({
      task_id: taskId,
      responded_by: user.id,
      selected_option_id: optionId,
      response_text: null,
    }, { onConflict: 'task_id,responded_by' });
    await supabase.from('jphph_tasks').update({ status: 'completed' }).eq('id', taskId);

    const { data: task } = await supabase.from('jphph_tasks').select('project_id, title').eq('id', taskId).maybeSingle();
    if (task) {
      await createNotification({
        project_id: task.project_id,
        notification_type: 'task',
        title: `Task completed: ${task.title}`,
        body: 'A selection has been made.',
        target_role: 'management',
        link_path: `/management/projects/${task.project_id}`,
        target_type: 'task',
        target_id: taskId,
        created_by: user.id,
      });
    }

    await loadTasks();
    setSelectedTask((prev) => prev?.id === taskId ? { ...prev, status: 'completed', myResponse: { id: '', task_id: taskId, responded_by: user.id, selected_option_id: optionId, response_text: null, created_at: '' } } : prev);
  }

  async function handleMarkPaid(taskId: string, ref: string, note: string) {
    await supabase.from('jphph_tasks').update({
      payment_status: 'paid',
      payment_reference: ref || null,
      payment_note: note || null,
      status: 'awaiting_response',
    }).eq('id', taskId);

    const { data: task } = await supabase.from('jphph_tasks').select('project_id, title').eq('id', taskId).maybeSingle();
    if (task) {
      await createNotification({
        project_id: task.project_id,
        notification_type: 'payment',
        title: `Payment marked: ${task.title}`,
        body: note || t('tasks.clientMarkedPaid'),
        target_role: 'management',
        link_path: `/management/projects/${task.project_id}`,
        target_type: 'task',
        target_id: taskId,
      });
    }

    await loadTasks();
    setSelectedTask(null);
  }

  async function handleConfirmPayment(taskId: string) {
    await supabase.from('jphph_tasks').update({ status: 'completed' }).eq('id', taskId);

    const { data: task } = await supabase.from('jphph_tasks').select('project_id, title').eq('id', taskId).maybeSingle();
    if (task) {
      await createNotification({
        project_id: task.project_id,
        notification_type: 'payment',
        title: `Payment confirmed: ${task.title}`,
        body: t('tasks.mgmtConfirmedReceipt'),
        target_role: 'client',
        link_path: `/client/payments`,
        target_type: 'task',
        target_id: taskId,
      });
    }

    await loadTasks();
    setSelectedTask(null);
  }

  async function handleRespond(taskId: string, text: string) {
    if (!user || !text.trim()) return;
    await supabase.from('jphph_task_responses').upsert({
      task_id: taskId,
      responded_by: user.id,
      selected_option_id: null,
      response_text: text.trim(),
    }, { onConflict: 'task_id,responded_by' });
    await supabase.from('jphph_tasks').update({ status: 'completed' }).eq('id', taskId);

    const { data: task } = await supabase.from('jphph_tasks').select('project_id, title').eq('id', taskId).maybeSingle();
    if (task) {
      await createNotification({
        project_id: task.project_id,
        notification_type: 'task',
        title: `Response received: ${task.title}`,
        body: text.trim(),
        target_role: 'management',
        link_path: `/management/projects/${task.project_id}`,
        target_type: 'task',
        target_id: taskId,
        created_by: user.id,
      });
    }

    await loadTasks();
    setSelectedTask(null);
  }

  const filtered = tasks.filter((t) => {
    if (activeFilter === 'All') return true;
    if (activeFilter === 'Open') return t.status === 'pending' || t.status === 'awaiting_response';
    if (activeFilter === 'Decision') return t.task_type === 'decision' || t.task_type === 'selection';
    if (activeFilter === 'Payment') return t.task_type === 'payment';
    if (activeFilter === 'Overdue') return t.status === 'overdue';
    if (activeFilter === 'Completed') return t.status === 'completed';
    return true;
  });

  const overdueCount = tasks.filter((t) => t.status === 'overdue').length;
  const openCount = tasks.filter((t) => t.status === 'pending' || t.status === 'awaiting_response').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 text-[#E8621A]">
            <i className="ri-loader-4-line text-3xl animate-spin"></i>
          </div>
          <p className="text-[#9CA3AF] text-sm">{t('tasks.loadingTasksLabel')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-full overflow-x-hidden">

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: t('tasks.openLabel'), value: openCount, color: '#E8621A', bg: '#FEF0E7' },
          { label: t('tasks.overdueLabel'), value: overdueCount, color: '#EF4444', bg: '#FEF2F2' },
          { label: t('tasks.totalLabel'), value: tasks.length, color: '#374151', bg: '#F9FAFB' },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl p-3 text-center" style={{ backgroundColor: s.bg }}>
            <div className="text-xl font-bold" style={{ color: s.color }}>{s.value}</div>
            <div className="text-xs font-medium mt-0.5" style={{ color: s.color }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs + Create */}
      <div className="flex items-center gap-2">
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 min-w-0 pb-0.5">
          {filterTabs.map((f) => (
            <button key={f.key} onClick={() => setActiveFilter(f.key)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${activeFilter === f.key ? 'bg-[#1A1A1A] text-white' : 'bg-white border border-gray-200 text-[#6B7280]'}`}>
              {f.label}{f.key === 'Overdue' && overdueCount > 0 ? ` (${overdueCount})` : ''}
            </button>
          ))}
        </div>
        {canCreate && (
          <button onClick={() => { resetCreateForm(); setShowCreateModal(true); }}
            className="flex items-center gap-1.5 bg-[#E8621A] text-white px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap flex-shrink-0">
            <i className="ri-add-line"></i>{t('tasks.newTaskBtn')}
          </button>
        )}
      </div>

      {/* Task cards */}
      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
            <i className="ri-task-line text-4xl"></i>
          </div>
          <div className="text-[#374151] font-semibold text-sm mb-1">{t('tasks.noTasksLabel')}</div>
          <div className="text-[#9CA3AF] text-xs">
            {canCreate ? t('tasks.createTaskLabel') : t('tasks.noTasksAssignedLabel')}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((task) => {
            const typeCfg = taskTypeConfig[task.task_type];
            const statusCfg = taskStatusConfig[task.status];
            const assigneeCfg = assigneeConfig[task.assignee_role];
            const isOverdue = task.status === 'overdue';

            return (
              <button key={task.id} onClick={() => setSelectedTask(task)}
                className={`w-full text-left rounded-2xl border-2 overflow-hidden cursor-pointer transition-all ${
                  isOverdue ? 'border-[#EF4444] bg-[#FEF2F2]' :
                  task.status === 'awaiting_response' ? 'border-[#E8621A]/30 bg-white' :
                  'border-gray-100 bg-white'
                }`}>
                {isOverdue && <div className="h-1 bg-[#EF4444]"></div>}
                {task.status === 'awaiting_response' && <div className="h-1 bg-[#E8621A]"></div>}
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: typeCfg.bg }}>
                      <i className={`${typeCfg.icon} text-base`} style={{ color: typeCfg.color }}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-semibold leading-snug ${isOverdue ? 'text-[#EF4444]' : 'text-[#1A1A1A]'}`}>{task.title}</p>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap" style={{ backgroundColor: statusCfg.bg, color: statusCfg.text }}>
                          {statusCfg.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: typeCfg.bg, color: typeCfg.color }}>{typeCfg.label}</span>
                        <div className="flex items-center gap-1">
                          <i className={`${assigneeCfg.icon} text-[10px]`} style={{ color: assigneeCfg.color }}></i>
                          <span className="text-[#9CA3AF] text-xs">{assigneeCfg.label}</span>
                        </div>
                        {task.due_date && (
                          <>
                            <span className="text-[#D1D5DB] text-xs">·</span>
                            <div className="flex items-center gap-1">
                              <i className={`ri-calendar-line text-[10px] ${isOverdue ? 'text-[#EF4444]' : 'text-[#9CA3AF]'}`}></i>
                              <span className={`text-xs ${isOverdue ? 'text-[#EF4444] font-semibold' : 'text-[#9CA3AF]'}`}>
                                {t('tasks.dueLabel')} {formatDate(task.due_date, lang)}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                      {task.task_type === 'payment' && task.invoice_amount && (
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-[#374151] text-sm font-bold">${task.invoice_amount.toLocaleString()}</span>
                          {task.payment_status && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: paymentStatusConfig[task.payment_status].bg, color: paymentStatusConfig[task.payment_status].text }}>
                              {paymentStatusConfig[task.payment_status].label}
                            </span>
                          )}
                        </div>
                      )}
                      {task.options && task.options.length > 0 && (
                        <div className="flex items-center gap-1 mt-1.5">
                          <i className="ri-list-check text-[#9CA3AF] text-[10px]"></i>
                          <span className="text-[#9CA3AF] text-xs">{task.options.length} {t('tasks.optionsCountLabel')}</span>
                          {task.myResponse?.selected_option_id && <span className="text-[#059669] text-xs font-semibold ml-1">· {t('tasks.selectedLabel')}</span>}
                        </div>
                      )}
                    </div>
                    <i className="ri-arrow-right-s-line text-[#D1D5DB] text-lg flex-shrink-0 mt-0.5"></i>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Task Detail Sheet */}
      {selectedTask && (
        <TaskDetailSheet
          task={selectedTask}
          viewerRole={viewerRole}
          canCreate={canCreate}
          onClose={() => setSelectedTask(null)}
          onSelectOption={handleSelectOption}
          onMarkPaid={handleMarkPaid}
          onConfirmPayment={handleConfirmPayment}
          onRespond={handleRespond}
          taskTypeConfig={taskTypeConfig}
          taskStatusConfig={taskStatusConfig}
          paymentStatusConfig={paymentStatusConfig}
          assigneeConfig={assigneeConfig}
          lang={lang}
        />
      )}

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[60] flex items-end lg:items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreateModal(false)}></div>
          <div className="relative bg-white w-full lg:max-w-lg rounded-t-3xl lg:rounded-3xl overflow-hidden max-h-[92vh] flex flex-col">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <h3 className="text-[#1A1A1A] font-bold text-base">{t('tasks.newTaskTitle')}</h3>
              <button onClick={() => setShowCreateModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-[#374151]"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-0">
              <div>
                <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('tasks.titleLabel')}</label>
                <input type="text" value={formTitle} onChange={(e) => setFormTitle(e.target.value)} placeholder={t('tasks.titlePlaceholderLabel')} className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]" />
              </div>
              <div>
                <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('tasks.taskTypeLabel')}</label>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.keys(taskTypeConfig) as TaskType[]).map((tp) => {
                    const cfg = taskTypeConfig[tp];
                    return (
                      <button key={tp} onClick={() => setFormType(tp)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 cursor-pointer text-left ${formType === tp ? 'border-[#E8621A]' : 'border-gray-100'}`}
                        style={formType === tp ? { backgroundColor: cfg.bg } : {}}>
                        <i className={`${cfg.icon} text-sm`} style={{ color: cfg.color }}></i>
                        <span className="text-xs font-medium" style={{ color: formType === tp ? cfg.color : '#6B7280' }}>{cfg.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('tasks.assignToLabel')}</label>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.keys(assigneeConfig) as AssigneeRole[]).map((r) => {
                    const cfg = assigneeConfig[r];
                    return (
                      <button key={r} onClick={() => setFormRole(r)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 cursor-pointer text-left ${formRole === r ? 'border-[#E8621A] bg-[#FEF0E7]' : 'border-gray-100'}`}>
                        <i className={`${cfg.icon} text-sm`} style={{ color: cfg.color }}></i>
                        <span className="text-xs font-medium" style={{ color: formRole === r ? '#E8621A' : '#6B7280' }}>{cfg.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('tasks.dueDateLabel')}</label>
                <input type="date" value={formDue} onChange={(e) => setFormDue(e.target.value)} className="w-full min-w-0 bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] block" style={{ maxWidth: '100%' }} />
              </div>
              <div>
                <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('tasks.descriptionLabel')}</label>
                <textarea value={formDesc} onChange={(e) => setFormDesc(e.target.value)} placeholder={t('tasks.descPlaceholderLabel')} rows={3} maxLength={500} className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] resize-none" />
              </div>

              {(formType === 'selection' || formType === 'decision') && (
                <div>
                  <label className="block text-[#555555] text-xs font-semibold mb-2">{t('tasks.optionsLabel2')}</label>
                  {formOptions.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2 mb-2 bg-[#F9FAFB] rounded-xl px-3 py-2.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-[#374151] text-sm font-medium">{opt.label}</p>
                        {opt.price_note && <p className="text-[#9CA3AF] text-xs">{opt.price_note}</p>}
                      </div>
                      <button onClick={() => setFormOptions((p) => p.filter((_, idx) => idx !== i))} className="text-[#9CA3AF] cursor-pointer">
                        <i className="ri-close-line text-base"></i>
                      </button>
                    </div>
                  ))}
                  <div className="space-y-2 mt-2">
                    <input type="text" value={newOptLabel} onChange={(e) => setNewOptLabel(e.target.value)} placeholder={t('tasks.optionLabelLabel')} className="w-full bg-white border border-[#E0DDD8] rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]" />
                    <input type="text" value={newOptDesc} onChange={(e) => setNewOptDesc(e.target.value)} placeholder={t('tasks.optionDescLabel')} className="w-full bg-white border border-[#E0DDD8] rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]" />
                    <input type="text" value={newOptPrice} onChange={(e) => setNewOptPrice(e.target.value)} placeholder={t('tasks.priceNoteLabel')} className="w-full bg-white border border-[#E0DDD8] rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]" />
                    <button onClick={() => { if (!newOptLabel.trim()) return; setFormOptions((p) => [...p, { label: newOptLabel, description: newOptDesc, price_note: newOptPrice }]); setNewOptLabel(''); setNewOptDesc(''); setNewOptPrice(''); }}
                      className="w-full py-2.5 rounded-xl border-2 border-dashed border-[#E0DDD8] text-[#9CA3AF] text-sm cursor-pointer hover:border-[#E8621A] hover:text-[#E8621A] transition-colors whitespace-nowrap">
                      {t('tasks.addOptionBtn')}
                    </button>
                  </div>
                </div>
              )}

              {formType === 'payment' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('tasks.invoiceNoLabel')}</label>
                      <input type="text" value={formInvNum} onChange={(e) => setFormInvNum(e.target.value)} placeholder={t('tasks.invoicePlaceholderLabel')} className="w-full min-w-0 bg-white border border-[#E0DDD8] rounded-xl px-3 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]" />
                    </div>
                    <div>
                      <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('tasks.amountLabel')}</label>
                      <input type="number" value={formInvAmt} onChange={(e) => setFormInvAmt(e.target.value)} placeholder={t('tasks.amountPlaceholderLabel')} className="w-full min-w-0 bg-white border border-[#E0DDD8] rounded-xl px-3 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('tasks.paymentDueDateLabel')}</label>
                    <input type="date" value={formPayDue} onChange={(e) => setFormPayDue(e.target.value)} className="w-full min-w-0 bg-white border border-[#E0DDD8] rounded-xl px-3 py-3 text-sm text-[#1A1A1A] block" style={{ maxWidth: '100%' }} />
                  </div>
                </div>
              )}

              {createError && <p className="text-[#EF4444] text-xs bg-[#FEF2F2] rounded-xl px-3 py-2">{createError}</p>}
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
              <button onClick={handleCreate} disabled={!formTitle.trim() || saving} className="w-full py-3.5 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <><i className="ri-loader-4-line animate-spin"></i>{t('tasks.creatingLabel')}</> : t('tasks.createTaskBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Task Detail Sheet ── */
interface TaskDetailSheetProps {
  task: ProjectTask;
  viewerRole: 'management' | 'subcontractor' | 'client';
  canCreate: boolean;
  onClose: () => void;
  onSelectOption: (taskId: string, optionId: string) => void;
  onMarkPaid: (taskId: string, ref: string, note: string) => void;
  onConfirmPayment: (taskId: string) => void;
  onRespond: (taskId: string, text: string) => void;
  taskTypeConfig: Record<TaskType, { label: string; icon: string; color: string; bg: string }>;
  taskStatusConfig: Record<TaskStatus, { label: string; bg: string; text: string }>;
  paymentStatusConfig: Record<PaymentStatus, { label: string; bg: string; text: string }>;
  assigneeConfig: Record<AssigneeRole, { label: string; color: string; icon: string }>;
  lang: string;
}

function TaskDetailSheet({ task, viewerRole, canCreate, onClose, onSelectOption, onMarkPaid, onConfirmPayment, onRespond, taskTypeConfig, taskStatusConfig, paymentStatusConfig, assigneeConfig, lang }: TaskDetailSheetProps) {
  const { t } = useTranslation();
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [responseText, setResponseText] = useState('');

  const typeCfg = taskTypeConfig[task.task_type];
  const statusCfg = taskStatusConfig[task.status];
  const assigneeCfg = assigneeConfig[task.assignee_role];
  const canRespond = viewerRole === task.assignee_role || task.assignee_role === 'all' || viewerRole === 'management';
  const isPaymentTask = task.task_type === 'payment';
  const isSelectionTask = task.task_type === 'selection' || task.task_type === 'decision';

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={onClose} />
      <div className="fixed z-[70] bg-white overflow-hidden bottom-0 left-0 right-0 rounded-t-3xl max-h-[92vh] lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:w-full lg:max-w-lg lg:rounded-3xl lg:max-h-[88vh] flex flex-col" style={{ boxShadow: '0 -4px 40px rgba(0,0,0,0.15)' }}>
        <div className="flex justify-center pt-3 pb-1 lg:hidden flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-200"></div>
        </div>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: typeCfg.bg }}>
              <i className={`${typeCfg.icon} text-lg`} style={{ color: typeCfg.color }}></i>
            </div>
            <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full" style={{ backgroundColor: typeCfg.bg, color: typeCfg.color }}>{typeCfg.label}</span>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 text-[#374151] cursor-pointer flex-shrink-0">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0 space-y-4">
          <div>
            <h2 className="text-[#1A1A1A] text-lg font-bold leading-snug mb-2">{task.title}</h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: statusCfg.bg, color: statusCfg.text }}>{statusCfg.label}</span>
              <div className="flex items-center gap-1">
                <i className={`${assigneeCfg.icon} text-[10px]`} style={{ color: assigneeCfg.color }}></i>
                <span className="text-[#6B7280] text-xs">{assigneeCfg.label}</span>
              </div>
              {task.due_date && (
                <div className="flex items-center gap-1">
                  <i className="ri-calendar-line text-[#9CA3AF] text-[10px]"></i>
                  <span className="text-[#9CA3AF] text-xs">{t('tasks.dueLabel')} {formatDate(task.due_date, lang)}</span>
                </div>
              )}
            </div>
          </div>

          {task.description && <p className="text-[#374151] text-sm leading-relaxed">{task.description}</p>}

          {isPaymentTask && (
            <div className="bg-[#F9FAFB] rounded-2xl p-4 space-y-2.5">
              <h4 className="text-[#1A1A1A] text-sm font-bold">{t('tasks.invoiceDetailsLabel')}</h4>
              {task.invoice_number && <div className="flex items-center justify-between"><span className="text-[#9CA3AF] text-sm">{t('tasks.invoiceNoLabel')}</span><span className="text-[#374151] text-sm font-semibold">{task.invoice_number}</span></div>}
              {task.invoice_amount && <div className="flex items-center justify-between"><span className="text-[#9CA3AF] text-sm">{t('tasks.amountLabel')}</span><span className="text-[#374151] text-sm font-semibold">${task.invoice_amount.toLocaleString()}</span></div>}
              {task.payment_due_date && <div className="flex items-center justify-between"><span className="text-[#9CA3AF] text-sm">{t('tasks.paymentDueDateLabel')}</span><span className="text-[#374151] text-sm font-semibold">{formatDate(task.payment_due_date, lang)}</span></div>}
              {task.payment_status && (
                <div className="flex items-center justify-between">
                  <span className="text-[#9CA3AF] text-sm">{t('tasks.paymentStatusLabel')}</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: paymentStatusConfig[task.payment_status].bg, color: paymentStatusConfig[task.payment_status].text }}>
                    {paymentStatusConfig[task.payment_status].label}
                  </span>
                </div>
              )}
              {task.payment_reference && <div className="flex items-center justify-between"><span className="text-[#9CA3AF] text-sm">{t('tasks.referenceLabel')}</span><span className="text-[#374151] text-sm font-medium">{task.payment_reference}</span></div>}
              {task.payment_note && <p className="text-[#6B7280] text-xs bg-white rounded-xl px-3 py-2">{task.payment_note}</p>}
            </div>
          )}

          {isSelectionTask && task.options && task.options.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-[#1A1A1A] text-sm font-bold">{t('tasks.optionsLabel2')}</h4>
              {task.options.map((opt) => {
                const isSelected = task.myResponse?.selected_option_id === opt.id;
                return (
                  <div key={opt.id} className={`rounded-2xl border-2 overflow-hidden transition-all ${isSelected ? 'border-[#059669]' : 'border-gray-100'}`}>
                    {opt.image_url && (
                      <button onClick={() => setLightboxUrl(opt.image_url!)} className="w-full h-36 overflow-hidden cursor-pointer">
                        <img src={opt.image_url} alt={opt.label} className="w-full h-full object-cover object-top" />
                      </button>
                    )}
                    <div className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-[#1A1A1A] text-sm font-semibold">{opt.label}</p>
                          {opt.description && <p className="text-[#6B7280] text-xs mt-0.5 leading-snug">{opt.description}</p>}
                          {opt.price_note && <p className="text-[#E8621A] text-xs font-semibold mt-1">{opt.price_note}</p>}
                        </div>
                        {isSelected && (
                          <div className="w-6 h-6 rounded-full bg-[#059669] flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}
                      </div>
                      {canRespond && task.status !== 'completed' && (
                        <button onClick={() => onSelectOption(task.id, opt.id)}
                          className={`w-full mt-3 py-2.5 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap transition-colors ${isSelected ? 'bg-[#059669] text-white' : 'bg-[#F9FAFB] text-[#374151] hover:bg-[#E8621A] hover:text-white'}`}>
                          {isSelected ? `${t('tasks.selectedLabel')} ✓` : t('tasks.selectThisOptionBtn')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {task.myResponse?.selected_option_id && (
                <div className="flex items-center gap-2 bg-[#F0FDF4] rounded-xl px-3 py-2.5">
                  <i className="ri-check-double-line text-[#059669] text-base"></i>
                  <p className="text-[#059669] text-sm font-semibold">{t('tasks.selectionConfirmedLabel')}</p>
                </div>
              )}
            </div>
          )}

          {task.myResponse?.response_text && (
            <div className="bg-[#F0FDF4] rounded-2xl p-4">
              <p className="text-[#059669] text-xs font-bold mb-1">{t('tasks.responseLabel')}</p>
              <p className="text-[#374151] text-sm leading-relaxed">{task.myResponse.response_text}</p>
            </div>
          )}

          {isPaymentTask && viewerRole === 'client' && task.payment_status === 'unpaid' && (
            <div className="space-y-3">
              <h4 className="text-[#1A1A1A] text-sm font-bold">{t('tasks.markAsPaidBtn')}</h4>
              <input type="text" value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} placeholder={t('tasks.paymentRefLabel')} className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]" />
              <input type="text" value={paymentNote} onChange={(e) => setPaymentNote(e.target.value)} placeholder={t('tasks.paymentNoteLabel')} className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]" />
              <button onClick={() => onMarkPaid(task.id, paymentRef, paymentNote)} className="w-full py-3.5 rounded-xl bg-[#059669] text-white text-sm font-bold cursor-pointer whitespace-nowrap">
                {t('tasks.markAsPaidBtn')}
              </button>
            </div>
          )}

          {isPaymentTask && viewerRole === 'management' && task.payment_status === 'paid' && task.status !== 'completed' && (
            <button onClick={() => onConfirmPayment(task.id)} className="w-full py-3.5 rounded-xl bg-[#059669] text-white text-sm font-bold cursor-pointer whitespace-nowrap">
              {t('tasks.confirmPaymentBtn')}
            </button>
          )}

          {!isPaymentTask && !isSelectionTask && canRespond && task.status !== 'completed' && task.status !== 'cancelled' && (
            <div className="space-y-3">
              <h4 className="text-[#1A1A1A] text-sm font-bold">{t('tasks.respondLabel')}</h4>
              <textarea value={responseText} onChange={(e) => setResponseText(e.target.value)} placeholder={t('tasks.typeResponseLabel')} rows={3} maxLength={500} className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] resize-none" />
              <button onClick={() => onRespond(task.id, responseText)} disabled={!responseText.trim()} className="w-full py-3.5 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50">
                {t('tasks.submitResponseBtn')}
              </button>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="w-full py-3 rounded-xl bg-[#F5F5F3] text-[#374151] text-sm font-semibold cursor-pointer whitespace-nowrap">{t('tasks.closeBtn')}</button>
        </div>
      </div>

      {lightboxUrl && (
        <div className="fixed inset-0 bg-black/95 z-[80] flex flex-col" onClick={() => setLightboxUrl(null)}>
          <div className="flex items-center justify-end px-4 py-4 flex-shrink-0">
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white cursor-pointer" onClick={() => setLightboxUrl(null)}>
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center px-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <img src={lightboxUrl} alt="Option" className="max-w-full max-h-full rounded-2xl object-contain" />
          </div>
          <div className="py-4"></div>
        </div>
      )}
    </>
  );
}